import 'antd/dist/reset.css';
import './scoped.css';

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Button as AntButton } from 'antd';
import { Helmet } from 'react-helmet-async';
import Joyride, {
  ACTIONS,
  EVENTS,
  STATUS,
} from 'react-joyride';
import { useNavigate } from 'react-router-dom';

import {
  ArrowOutward as ArrowOutwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';

import { ReactComponent as PillBuildEvidenceIcon } from '../../img/llm/pill_build_evidence.svg';
import { ReactComponent as PillCheckClaimIcon } from '../../img/llm/pill_check_claim.svg';
import { ReactComponent as PillCompareOptionsIcon } from '../../img/llm/pill_compare_options.svg';
import { ReactComponent as PillFocusHypothesisIcon } from '../../img/llm/pill_focus_hypothesis.svg';
import { fetchUsage, limitReachedText } from '../../service/credits';
import { isRunActive, subscribeToActiveRun } from '../../service/activeRun';
import { trackGtagEvent } from '../../utils/gtag';
import { useAuth } from '../Auth/AuthContext';
import exampleSchema from './exampleSchema.json';
import LlmSearchBar from './LlmSearchBarHome';
import NewsStrip from './NewsStrip';

// const { Search } = Input;
const DEBUG_FORCE_LIMIT_WARNING = false;

const isPhoneViewport = () => window.matchMedia('(max-width: 767px)').matches;


const HomePage = () => {
    // const [tags, setTags] = useState([]);
    const [runTour, setRunTour] = useState(false);
    const [searchBarOpen, setSearchBarOpen] = useState(false);
    const [isAutocompleteExamplesOpen, setIsAutocompleteExamplesOpen] = useState(false);
    const [showExamples, setShowExamples] = useState(undefined);
    const [prefillQuery, setPrefillQuery] = useState('');
    const [isQueryLimitReached, setIsQueryLimitReached] = useState(false);
    // What the reader has left — credits signed in, free questions as a guest (service/credits.js).
    const [usage, setUsage] = useState(null);
    const [isPhoneDevice, setIsPhoneDevice] = useState(isPhoneViewport);
    const [isAgentRunActive, setIsAgentRunActive] = useState(() => isRunActive());
    // The composer's mode (Chat / Investigate / Literature Review): what shows under it follows.
    const [composerMode, setComposerMode] = useState('chat');
    const [requestedMode, setRequestedMode] = useState(null);
    const modeExamples = composerMode === 'investigate' ? (exampleSchema.investigateExamples || [])
        : composerMode === 'review' ? (exampleSchema.reviewExamples || []) : null;
    const tryNews = (item) => {
        if (item.mode) setRequestedMode({ mode: item.mode, at: Date.now() });
        if (item.focus) {
            setTimeout(() => document.querySelector('.llm-searchbar textarea')?.focus(), 0);
        }
    };
    const { isAuthenticated, loading, openLoginModal } = useAuth();
    const navigate = useNavigate();
    const examplePanelRef = useRef(null);
    const heroRef = useRef(null);
    const heroInnerRef = useRef(null);
    const [heroTopOffset, setHeroTopOffset] = useState(null);
    const iconMap = {
        lightbulb: <PillFocusHypothesisIcon />,
        chart: <PillCompareOptionsIcon />,
        book: <PillCheckClaimIcon />,
        knowledge: <PillBuildEvidenceIcon />,
    };
    const pills = (exampleSchema.pills || []).map((pill) => ({
        ...pill,
        icon: iconMap[pill.icon] || <PillFocusHypothesisIcon />,
    }));
    const activePill = pills.find((pill) => pill.id === showExamples);
    const isHomeLimitReachedEffective = isQueryLimitReached || DEBUG_FORCE_LIMIT_WARNING;
    const showHomeLimitWarning = isHomeLimitReachedEffective;
    const limitText = limitReachedText(usage);

    useEffect(() => subscribeToActiveRun(
        (run) => setIsAgentRunActive(Boolean(run)),
    ), []);

    // const [focused, setFocused] = useState(false);
    // const theme = useTheme();
    // const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!showExamples) {
            return;
        }

        const handleClickOutside = (event) => {
            if (examplePanelRef.current && !examplePanelRef.current.contains(event.target)) {
                setShowExamples(undefined);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showExamples]);

    useEffect(() => {
        const evaluateIsPhone = () => {
            setIsPhoneDevice(isPhoneViewport());
        };

        evaluateIsPhone();
        window.addEventListener('resize', evaluateIsPhone);
        return () => {
            window.removeEventListener('resize', evaluateIsPhone);
        };
    }, []);

    useLayoutEffect(() => {
        if (!heroRef.current || !heroInnerRef.current) return;
        const heroRect = heroRef.current.getBoundingClientRect();
        const innerRect = heroInnerRef.current.getBoundingClientRect();
        setHeroTopOffset(innerRect.top - heroRect.top);
    }, []);

    useEffect(() => {
        let active = true;

        const loadTier = async () => {
            if (loading) {
                if (active) setIsQueryLimitReached(false);
                return;
            }

            const next = await fetchUsage({ isAuthenticated });
            if (!active || !next) return;
            setUsage(next);
            setIsQueryLimitReached(next.limitReached);
        };

        loadTier();
        return () => {
            active = false;
        };
    }, [isAuthenticated, loading]);

    useEffect(() => {
        if (showHomeLimitWarning) {
            setShowExamples(undefined);
        }
    }, [showHomeLimitWarning]);



    // const handleSearch = async (v) => {
    //     // Track searches
    //     trackEvent('Search', 'Search Performed', activeButton);

    //     navigate('/result', {
    //         state: {
    //             search_data: v,
    //             searchType: activeButton
    //         }
    //     });
    // }

    // const handleExampleQuery = (index) => {
    //     // Track example query clicks
    //     trackEvent('Search', 'Example Query Click', `Example ${index + 1}`);

    //     if (activeButton === 'triplet') {
    //         if (exampleQueries && exampleQueries.length > index) {
    //             const exampleQuery = exampleQueries[index];
    //             // Fill the search bar instead of navigating
    //             if (searchBarKnowledgeRef.current) {
    //                 searchBarKnowledgeRef.current.fillWithExample(exampleQuery);
    //             }
    //         }
    //     } else {
    //         if (neighborhoodExamples && neighborhoodExamples.length > index) {
    //             const exampleQuery = neighborhoodExamples[index];
    //             // Fill the search bar instead of navigating
    //             if (searchBarNeighborhoodRef.current) {
    //                 searchBarNeighborhoodRef.current.fillWithExample(exampleQuery);
    //             }
    //         }
    //     }
    // }

    const handleJoyrideCallback = (data) => {
        const { status, type, action } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setRunTour(false);
        }

        // Automatically advance to the next step when the tour starts
        if (type === EVENTS.STEP_AFTER && action === ACTIONS.START) {
            setRunTour(true);
        }
    };

    const getTourSteps = () => {
        const commonSteps = [
            {
                target: '.glkb-title',
                content: 'Welcome to GLKB AI Chat. Ask questions and explore biomedical literature with our agent.',
                placement: 'bottom',
                disableBeacon: true,
            },
        ];

        const llmSteps = [
            {
                target: '.llm-searchbar',
                content: 'Ask any question about biomedical literature. Our AI agent will help find and analyze relevant information for you.',
                placement: 'bottom',
            },
            {
                target: '.homepage-pills',
                content: 'Start with a sample question to see how GLKB works. \nYou can also type your own question anytime.',
                placement: 'bottom',
            }
        ];

        return [
            ...commonSteps,
            ...llmSteps,
        ];
    };

    // Update steps when activeButton changes
    const steps = getTourSteps();

    return (
        <>
            <Helmet>
                <title>GLKB: Genomic Literature Knowledge Base</title>
                <meta name="description" content="Weeks of research, done in minutes. Save time with AI that answers complex biomedical questions using authoritative scientific literature. Every answer is grounded in published research and supported by transparent citations." />
                <meta name="title" content="GLKB: Genomic Literature Knowledge Base" />
                <meta property="og:title" content="GLKB: Genomic Literature Knowledge Base" />
                <meta property="og:site_name" content="GLKB: Genomic Literature Knowledge Base" />
                <meta name="twitter:title" content="GLKB: Genomic Literature Knowledge Base" />
                <meta property="og:description" content="Weeks of research, done in minutes. Save time with AI that answers complex biomedical questions using authoritative scientific literature. Every answer is grounded in published research and supported by transparent citations." />
            </Helmet>
            <div className={`HomePageRoot${isPhoneDevice ? ' is-phone-device' : ''}`}>
                <div className="HomePageContainer">
                    <div className="HomePageInner" style={{
                        backgroundColor: 'var(--color-background-surface)',
                        transition: 'background-color 0.3s ease',
                    }}>
                        <Joyride
                            steps={steps}
                            run={runTour}
                            continuous={true}
                            showSkipButton={true}
                            showProgress={true}
                            callback={handleJoyrideCallback}
                            styles={{
                                options: {
                                    primaryColor: 'var(--color-brand-primary)',
                                    zIndex: 10000
                                },
                                tooltip: {
                                    textAlign: 'left',
                                    content: {
                                        textAlign: 'left'
                                    }
                                },
                                tooltipContent: {
                                    textAlign: 'left',
                                    fontFamily: 'Geist, sans-serif',
                                }
                            }}
                            locale={{
                                last: 'Close', // Change the text of the final button to "Close"
                                next: 'Next',
                                back: 'Back',
                                skip: 'Skip',
                            }}
                            disableOverlayClose={true}
                            disableBeacon={true}
                            disableCloseOnEsc={true}
                            disableScrolling={true}
                            spotlightClicks={true}
                            spotlightPadding={0}
                            scrollToFirstStep={true}
                        />
                        <div className="news-strip-slot">
                            <NewsStrip onTry={tryNews} compact={isPhoneDevice} />
                        </div>
                        <Box
                            className="homepage-hero"
                            ref={heroRef}
                            style={heroTopOffset !== null ? { justifyContent: 'flex-start', paddingTop: `${heroTopOffset}px` } : undefined}
                        >
                            <Box className="homepage-hero-inner" ref={heroInnerRef}>
                                <Typography
                                    className="glkb-title"
                                    sx={{
                                        fontFamily: 'Geist, sans-serif',
                                        fontWeight: 800,
                                        letterSpacing: isPhoneDevice ? '-0.01em' : undefined,
                                        fontSize: isPhoneDevice ? '32px' : '40px',
                                        lineHeight: isPhoneDevice ? '40px' : '48px',
                                    }}
                                >
                                    <span style={{ color: 'var(--color-text-primary)' }}>Ask.</span>{' '}
                                    <span style={{ color: 'var(--color-brand-primary)' }}>Analyze.</span>{' '}
                                    <span style={{ color: 'var(--color-text-primary)' }}>Cite.</span>
                                </Typography>
                                <Typography
                                    className="glkb-subtitle"
                                    sx={{
                                        fontFamily: 'Geist, sans-serif',
                                        fontWeight: 400,
                                        fontSize: isPhoneDevice ? '15px' : '14px',
                                        color: 'var(--color-text-tertiary)',
                                        lineHeight: '22px',
                                    }}
                                >
                                    Weeks of research, done in minutes.
                                </Typography>
                            </Box>
                            <Box className="homepage-hero-search">
                                <Box className="search-bar">
                                    <Box className="homepage-search-stack">
                                        {showHomeLimitWarning && (
                                            <Box className="homepage-limit-warning">
                                                <span className="homepage-limit-warning-text">
                                                    {limitText}
                                                </span>
                                                {!isAuthenticated && !loading && (
                                                    <button
                                                        type="button"
                                                        className="homepage-limit-warning-button"
                                                        onClick={() => openLoginModal(limitText)}
                                                    >
                                                        Sign in
                                                    </button>
                                                )}
                                            </Box>
                                        )}
                                        <LlmSearchBar
                                            setOpen={setSearchBarOpen}
                                            setExamplesOpen={setIsAutocompleteExamplesOpen}
                                            onCollapseExampleLists={() => {
                                                setShowExamples(undefined);
                                                setIsAutocompleteExamplesOpen(false);
                                            }}
                                            prefillQuery={prefillQuery}
                                            // The three starter questions are chat questions; Investigate and Literature
                                            // Review do not open them (their examples show under the composer).
                                            autocompleteOptions={composerMode === 'chat' ? (exampleSchema.autocomplete || []) : []}
                                            isQueryLimitReached={showHomeLimitWarning}
                                            limitReachedText={limitText}
                                            isAgentRunActive={isAgentRunActive}
                                            onModeChange={setComposerMode}
                                            requestedMode={requestedMode}
                                        />
                                        {activePill && (
                                            <Paper className="homepage-examples-panel" ref={examplePanelRef}>
                                                <Box className="homepage-examples-header">
                                                    <Typography className="homepage-examples-title">
                                                        {activePill.label}
                                                    </Typography>
                                                    <IconButton
                                                        aria-label="Close examples"
                                                        size="small"
                                                        onClick={() => {
                                                            trackGtagEvent('home_examples_close_click', {
                                                                section: activePill?.id || '',
                                                            });
                                                            setShowExamples(undefined);
                                                        }}
                                                        className="homepage-examples-close"
                                                    >
                                                        <CloseIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                                <Box className="homepage-examples-list">
                                                    {(activePill.examples || []).map((example) => (
                                                        <button
                                                            key={example}
                                                            type="button"
                                                            className="homepage-examples-item"
                                                            onClick={() => {
                                                                trackGtagEvent('home_example_item_click', {
                                                                    section: activePill?.id || '',
                                                                    label: activePill?.label || '',
                                                                });
                                                                setPrefillQuery(example);
                                                                setShowExamples(undefined);
                                                            }}
                                                        >
                                                            <span>{example}</span>
                                                            <span className="homepage-examples-arrow">
                                                                <ArrowOutwardIcon fontSize="small" />
                                                            </span>
                                                        </button>
                                                    ))}
                                                </Box>
                                            </Paper>
                                        )}
                                    </Box>
                                </Box>
                                {modeExamples ? (
                                    <Box className={`homepage-mode-examples is-${composerMode}`}>
                                        {modeExamples.map((example) => (
                                            <button
                                                key={example}
                                                type="button"
                                                className="homepage-mode-example"
                                                onClick={() => {
                                                    trackGtagEvent('home_mode_example_click', { mode: composerMode });
                                                    setPrefillQuery(example);
                                                }}
                                            >
                                                {example}
                                            </button>
                                        ))}
                                    </Box>
                                ) : (
                                <Box className={`homepage-pills${(showExamples || isAutocompleteExamplesOpen) ? ' is-hidden' : ''}${showHomeLimitWarning ? ' has-limit-warning' : ''}`}>
                                    {pills.map((pill) => (
                                        <Box
                                            key={pill.id}
                                            className={`homepage-pill${showExamples === pill.id ? ' is-active' : ''}${showHomeLimitWarning ? ' is-disabled' : ''}`}
                                            onClick={(event) => {
                                                if (showHomeLimitWarning) {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    return;
                                                }
                                                // Open to guests (2026-10-09): an example only fills the box,
                                                // and sending it is a question like any other.
                                                trackGtagEvent('home_example_group_click', {
                                                    section: pill.id,
                                                    label: pill.label,
                                                });
                                                setShowExamples((current) => current === pill.id ? undefined : pill.id);
                                            }}
                                        >
                                            <span className="homepage-pill-icon">{pill.icon}</span>
                                            <span className="homepage-pill-label">{pill.label}</span>
                                        </Box>
                                    ))}
                                </Box>
                                )}
                                <p className="homepage-research-notice">
                                    For research use only — not medical advice. AI-generated; verify all citations against primary sources.
                                </p>
                            </Box>
                        </Box>

                        <div className="footer">
                            <div style={{ width: '100%', margin: '0 auto', padding: '0 0px' }}>
                                <p className="homepage-footer-line">
                                    © 2026 GLKB — Genomic Literature Knowledge Base · glkb.org
                                </p>
                                <p className="homepage-footer-line">
                                    Developed and maintained by the <a className="homepage-lab-link" href="https://jieliu6.github.io/" target="_blank" rel="noopener noreferrer">Jie Liu Lab</a>, Department of Computational Medicine and Bioinformatics, University of Michigan.
                                </p>
                                <p className="homepage-footer-line">
                                    Includes data from PubMed®/MEDLINE® (NLM); GLKB is independent and not endorsed by NLM or the federal government.
                                </p>
                            </div>
                        </div>
                    </div>
                    <AntButton
                        onClick={() => {
                            trackGtagEvent('home_tour_open_click', { source: 'floating_help_button' });
                            setRunTour(true);
                        }}
                        // style={{ marginTop: '20px' }}
                        style={{
                            position: 'fixed',
                            bottom: isPhoneDevice ? '100px' : '24px',
                            right: '20px',
                            width: '56px',
                            height: '56px',
                            fontSize: '24px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-brand-soft)',
                            color: 'var(--color-brand-primary)',
                            border: 'none',
                            fontFamily: 'Geist, sans-serif',
                            boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px rgba(0, 0, 0, 0.10)',
                        }}
                    >
                        ?
                    </AntButton>
                </div>
            </div>
        </>
    )
}

export default HomePage
